import { AssignedParticipantUnreadInfo, CadFeatureMap, ConversationView } from '@mark43/cad-api';
import { playSound } from '../../sounds/play-sound';
import { ParticipantIdentities } from '../store/types';
import { getRecordKeys } from '@m43/core';
import { isActiveConversation } from '../store/helpers';
import { electronBridge } from '../../electron-bridge/electron-bridge';

export const hasPreferencesToPlayAlertOnNewMessage = ({
    isUrgentMessage,
    enabledFeatures,
    isCad,
}: {
    isUrgentMessage: boolean;
    enabledFeatures?: Partial<CadFeatureMap>;
    isCad?: boolean;
}) => {
    const {
        dispatchPlayAlertOnNewUrgentMessage,
        dispatchPlayAlertOnNewStandardMessage,
        firstResponderPlayAlertOnNewUrgentMessage,
        firstResponderPlayAlertOnNewStandardMessage,
    } = enabledFeatures ?? {};

    if (isCad) {
        return isUrgentMessage
            ? !!dispatchPlayAlertOnNewUrgentMessage
            : !!dispatchPlayAlertOnNewStandardMessage;
    } else {
        return isUrgentMessage
            ? !!firstResponderPlayAlertOnNewUrgentMessage
            : !!firstResponderPlayAlertOnNewStandardMessage;
    }
};

export const shouldPlayNewMessageSoundForParticipant = ({
    enabledFeatures,
    participant,
    isCad,
}: {
    enabledFeatures?: Partial<CadFeatureMap>;
    participant: AssignedParticipantUnreadInfo;
    isCad?: boolean;
}) => {
    const { unreadCount, hasStandardUnread, hasUrgentUnread } = participant;
    const commonProps = { enabledFeatures, isCad };

    if (unreadCount === 0) {
        return false;
    }

    return (
        (hasStandardUnread &&
            hasPreferencesToPlayAlertOnNewMessage({ ...commonProps, isUrgentMessage: false })) ||
        (hasUrgentUnread &&
            hasPreferencesToPlayAlertOnNewMessage({ ...commonProps, isUrgentMessage: true }))
    );
};

const shouldPlayNewMessageSoundForConversation = ({
    enabledFeatures,
    conversation,
    isCad,
}: {
    enabledFeatures?: Partial<CadFeatureMap>;
    conversation: ConversationView;
    isCad?: boolean;
}) => {
    return conversation.assignedParticipantUnreadInfos.some((participant) =>
        shouldPlayNewMessageSoundForParticipant({
            enabledFeatures,
            participant,
            isCad,
        })
    );
};

export const shouldPlayNewMessageSoundForConversations = ({
    previousConversations,
    currentConversations,
    participantIdentities,
    enabledFeatures = {},
    isCad,
}: {
    previousConversations?: Record<number, ConversationView>;
    currentConversations?: Record<number, ConversationView>;
    participantIdentities?: ParticipantIdentities;
    enabledFeatures: Partial<CadFeatureMap>;
    isCad?: boolean;
}) => {
    const {
        dispatchPlayAlertOnNewStandardMessage,
        dispatchPlayAlertOnNewUrgentMessage,
        firstResponderPlayAlertOnNewStandardMessage,
        firstResponderPlayAlertOnNewUrgentMessage,
    } = enabledFeatures;

    if (!previousConversations || !currentConversations || !participantIdentities) {
        return false;
    }
    if (isCad && !dispatchPlayAlertOnNewStandardMessage && !dispatchPlayAlertOnNewUrgentMessage) {
        return false;
    }
    if (
        !isCad &&
        !firstResponderPlayAlertOnNewStandardMessage &&
        !firstResponderPlayAlertOnNewUrgentMessage
    ) {
        return false;
    }
    const conversationIds = getRecordKeys(currentConversations);
    return conversationIds.some((id) => {
        const conversation = currentConversations[id];
        if (!isActiveConversation(conversation, participantIdentities)) {
            return false;
        }
        return shouldPlayNewMessageSoundForConversation({
            enabledFeatures,
            conversation,
            isCad,
        });
    });
};

export const playNewMessageSound = async () => {
    if (electronBridge) {
        // in electron we use window.requestToPlayAudioAlert() to avoid playing sound in multiple windows
        if (await electronBridge.requestToPlayAudioAlert()) {
            playSound('new-message-received');
        }
    } else {
        playSound('new-message-received');
    }
};
